// Spec: specs/stats/questions/data_lab/storytelling_notes/functional_requirements.md §Draft lifecycle
//       specs/stats/questions/functional_requirements.md §7 Persistence

export const DRAFT_KEY_PREFIX = 'AYTM-Draft'

const DRAFT_VERSION = 1

export interface Draft {
  text: string
  note: string
  asset: Record<string, unknown> | null
  imageSlide: boolean
}

interface DraftRecord extends Draft {
  v: typeof DRAFT_VERSION
}

// kind keeps one editor's drafts apart from another's: `note` for storytelling notes, `annotation` for question annotations
export interface DraftKeyParts {
  kind: string
  surveyId: string | number
  userId: string | number
  reportId: string | number
  id: string
}

export interface DraftStore {
  read(key: string): Draft | null
  write(key: string, draft: Draft, options?: { memoryOnly?: boolean }): void
  remove(key: string): void
  // keep receives the report id and the item id as they appear in the key; returns the removed keys
  removeStale(
    kind: string,
    surveyId: string | number,
    userId: string | number,
    keep: (reportId: string, id: string) => boolean
  ): string[]
}

export const draftKey = ({ kind, surveyId, userId, reportId, id }: DraftKeyParts): string =>
  [DRAFT_KEY_PREFIX, kind, surveyId, userId, reportId, id].join('/')

const pickDraftFields = ({ text, note, asset, imageSlide }: Draft): Draft => ({ text, note, asset, imageSlide })

const isRecord = (value: unknown): value is DraftRecord => {
  if (!value || 'object' !== typeof value) return false
  const { v, text, note, asset, imageSlide } = value as Record<string, unknown>
  return (
    DRAFT_VERSION === v &&
    'string' === typeof text &&
    'string' === typeof note &&
    'boolean' === typeof imageSlide &&
    (null === asset || ('object' === typeof asset && !Array.isArray(asset)))
  )
}

const parseRecord = (raw: string): DraftRecord | null => {
  try {
    const record = JSON.parse(raw)
    return isRecord(record) ? record : null
  } catch {
    return null
  }
}

const removeStored = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key)
  } catch {
    // nothing more to do when storage cannot be reached
  }
}

export const createDraftStore = (getStorage: () => Storage | null = () => window.localStorage): DraftStore => {
  const memory = new Map<string, DraftRecord>()

  // reaching localStorage throws when the browser blocks site data
  const storage = (): Storage | null => {
    try {
      return getStorage()
    } catch {
      return null
    }
  }

  const read = (key: string): Draft | null => {
    const inMemory = memory.get(key)
    if (inMemory) return pickDraftFields(inMemory)

    const store = storage()
    if (!store) return null

    let raw: string | null
    try {
      raw = store.getItem(key)
    } catch {
      return null
    }
    if (null === raw) return null

    const record = parseRecord(raw)
    if (!record) {
      removeStored(store, key)
      return null
    }
    return pickDraftFields(record)
  }

  // an older stored copy left behind would come back after a reload, while a memory-held draft must end with the page
  const write = (key: string, draft: Draft, { memoryOnly = false } = {}): void => {
    const record: DraftRecord = { v: DRAFT_VERSION, ...pickDraftFields(draft) }
    memory.set(key, record)

    const store = storage()
    if (!store) return

    if (memoryOnly) {
      removeStored(store, key)
      return
    }

    try {
      store.setItem(key, JSON.stringify(record))
    } catch {
      removeStored(store, key)
    }
  }

  const remove = (key: string): void => {
    memory.delete(key)
    const store = storage()
    if (store) removeStored(store, key)
  }

  const removeStale: DraftStore['removeStale'] = (kind, surveyId, userId, keep) => {
    const prefix = `${DRAFT_KEY_PREFIX}/${kind}/${surveyId}/${userId}/`
    const keys = new Set<string>(Array.from(memory.keys()).filter(key => key.startsWith(prefix)))

    const store = storage()
    if (store) {
      try {
        for (let index = 0; index < store.length; index++) {
          const key = store.key(index)
          if (key?.startsWith(prefix)) keys.add(key)
        }
      } catch {
        // storage became unreachable mid-listing; remove what was listed
      }
    }

    // collected first: removing while walking storage indexes would skip keys
    const removed: string[] = []
    keys.forEach(key => {
      const rest = key.slice(prefix.length)
      const cut = rest.lastIndexOf('/')
      if (0 > cut) return
      if (keep(rest.slice(0, cut), rest.slice(cut + 1))) return
      remove(key)
      removed.push(key)
    })
    return removed
  }

  return { read, write, remove, removeStale }
}

export default createDraftStore()
